import { useCallback, useEffect, useRef, useState } from 'react';

interface IdleTimeoutOptions {
  /** Total inactivity (ms) before onTimeout fires. */
  timeoutMs: number;
  /** How long (ms) before the timeout to raise the warning. */
  warningMs: number;
  /** Called once when the idle window fully elapses. */
  onTimeout: () => void;
  /** Disable the timer (e.g. while not authenticated). Default true. */
  enabled?: boolean;
}

/**
 * Client-side inactivity auto-logout (Bug-0022).
 *
 * Any real user activity (pointer, keyboard, scroll, touch) resets the idle
 * clock. Once `timeoutMs - warningMs` of inactivity passes we surface a
 * warning + a live countdown; at `timeoutMs` we fire `onTimeout` exactly once.
 * Activity during the warning dismisses it; `stayActive()` resets the clock on
 * demand (the "Stay signed in" button).
 *
 * This complements the server-side session expiry — it enforces a tighter,
 * user-visible inactivity window and gives the redirect-to-login UX QA asked
 * for; the server remains the real boundary.
 */
export function useIdleTimeout({
  timeoutMs,
  warningMs,
  onTimeout,
  enabled = true,
}: IdleTimeoutOptions): {
  warning: boolean;
  secondsLeft: number;
  stayActive: () => void;
} {
  const lastActivity = useRef<number>(Date.now());
  const fired = useRef(false);
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // Keep the latest callback without re-subscribing the interval each render.
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const stayActive = useCallback(() => {
    lastActivity.current = Date.now();
    fired.current = false;
    setWarning(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    lastActivity.current = Date.now();
    fired.current = false;

    const bump = () => {
      if (fired.current) return;
      lastActivity.current = Date.now();
    };
    const events = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
    ] as const;
    events.forEach((e) =>
      window.addEventListener(e, bump, { passive: true }),
    );

    const id = window.setInterval(() => {
      const elapsed = Date.now() - lastActivity.current;
      if (elapsed >= timeoutMs) {
        if (!fired.current) {
          fired.current = true;
          setWarning(false);
          onTimeoutRef.current();
        }
        return;
      }
      if (elapsed >= timeoutMs - warningMs) {
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((timeoutMs - elapsed) / 1000)));
      } else {
        setWarning(false);
      }
    }, 1000);

    return () => {
      window.clearInterval(id);
      events.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [enabled, timeoutMs, warningMs]);

  return { warning, secondsLeft, stayActive };
}
